/**
 * Content Upload Modal
 * Handles uploading new content (flipbooks, images, videos, files)
 */

import { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import {
  createFlipbook,
  createImage,
  createVideoLink,
  createFile,
  createFormattedText,
} from "../../services/content.service";
import type { PDFProcessingProgress } from "../../utils/pdfProcessor";
import { useToast } from "../ui/Toast";
import Button from "../common/Button";
import type { ContentType } from "../../types/content.types";
import { ContentType as ContentTypeEnum } from "../../types/content.types";
import {
  XMarkIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  PaperClipIcon,
  DocumentTextIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import RichTextEditor from "./RichTextEditor";
import clsx from "clsx";

interface ContentUploadModalProps {
  initialType?: ContentType | null;
  onClose: () => void;
  onComplete: () => void;
}

export default function ContentUploadModal({
  initialType,
  onClose,
  onComplete,
}: ContentUploadModalProps) {
  const { user } = useAuth();
  const { showToast } = useToast();

  // Form state
  const [contentType, setContentType] = useState<ContentType>(
    initialType || ContentTypeEnum.IMAGE,
  );
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [isPublic, setIsPublic] = useState(false);

  // File upload state
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Video URL state
  const [videoUrl, setVideoUrl] = useState("");

  // Formatted text state
  const [textContent, setTextContent] = useState("");

  // Upload/processing progress
  const [processingProgress, setProcessingProgress] =
    useState<PDFProcessingProgress | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Create mutations for each content type
  const flipbookMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      setIsProcessing(true);
      return createFlipbook(file, title, user.id, {
        description,
        tags,
        is_public: isPublic,
        onProgress: (progress) => {
          setProcessingProgress(progress);
        },
      });
    },
    onSuccess: () => {
      setIsProcessing(false);
      showToast("success", "Flipbook uploaded and processing started");
      onComplete();
    },
    onError: (error: Error) => {
      setIsProcessing(false);
      showToast("error", `Failed to upload: ${error.message}`);
    },
  });

  const imageMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      return createImage(file, title, user.id, {
        description,
        tags,
        is_public: isPublic,
      });
    },
    onSuccess: () => {
      showToast("success", "Image uploaded successfully");
      onComplete();
    },
    onError: (error: Error) => {
      showToast("error", `Failed to upload: ${error.message}`);
    },
  });

  const videoMutation = useMutation({
    mutationFn: async (url: string) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      return createVideoLink(url, title, user.id, {
        description,
        tags,
        is_public: isPublic,
      });
    },
    onSuccess: () => {
      showToast("success", "Video link added successfully");
      onComplete();
    },
    onError: (error: Error) => {
      showToast("error", `Failed to add video: ${error.message}`);
    },
  });

  const fileMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      return createFile(file, title, user.id, {
        description,
        tags,
        is_public: isPublic,
      });
    },
    onSuccess: () => {
      showToast("success", "File uploaded successfully");
      onComplete();
    },
    onError: (error: Error) => {
      showToast("error", `Failed to upload: ${error.message}`);
    },
  });

  const textBlockMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      return createFormattedText(title, content, user.id, {
        description,
        tags,
        is_public: isPublic,
      });
    },
    onSuccess: () => {
      showToast("success", "Formatted text created successfully");
      onComplete();
    },
    onError: (error: Error) => {
      showToast("error", `Failed to create: ${error.message}`);
    },
  });

  const isLoading =
    flipbookMutation.isPending ||
    imageMutation.isPending ||
    videoMutation.isPending ||
    fileMutation.isPending ||
    textBlockMutation.isPending ||
    isProcessing;

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  }, []);

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    if (!title) {
      // Auto-fill title from filename
      const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, "");
      setTitle(nameWithoutExt);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      showToast("error", "Please enter a title");
      return;
    }

    if (contentType === ContentTypeEnum.TEXT_BLOCK) {
      if (!textContent.trim() || textContent === "<p></p>") {
        showToast("error", "Please enter some content");
        return;
      }
      await textBlockMutation.mutateAsync(textContent);
    } else if (contentType === ContentTypeEnum.VIDEO_LINK) {
      if (!videoUrl.trim()) {
        showToast("error", "Please enter a video URL");
        return;
      }
      await videoMutation.mutateAsync(videoUrl);
    } else {
      if (!file) {
        showToast("error", "Please select a file");
        return;
      }

      switch (contentType) {
        case ContentTypeEnum.FLIPBOOK:
          await flipbookMutation.mutateAsync(file);
          break;
        case ContentTypeEnum.IMAGE:
          await imageMutation.mutateAsync(file);
          break;
        case ContentTypeEnum.FILE:
          await fileMutation.mutateAsync(file);
          break;
      }
    }
  };

  const getAcceptedFileTypes = () => {
    switch (contentType) {
      case ContentTypeEnum.FLIPBOOK:
        return ".pdf";
      case ContentTypeEnum.IMAGE:
        return "image/*";
      case ContentTypeEnum.FILE:
        return "*";
      default:
        return "*";
    }
  };

  const getFileTypeDescription = () => {
    switch (contentType) {
      case ContentTypeEnum.FLIPBOOK:
        return "PDF files only";
      case ContentTypeEnum.IMAGE:
        return "JPG, PNG, GIF, WebP";
      case ContentTypeEnum.FILE:
        return "Any file type";
      default:
        return "";
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Add New Content
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-gray-500 hover:bg-gray-100"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Content Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content Type
              </label>
              <div className="grid grid-cols-5 gap-2">
                {[
                  {
                    type: ContentTypeEnum.FLIPBOOK,
                    icon: DocumentIcon,
                    label: "Flipbook",
                  },
                  {
                    type: ContentTypeEnum.IMAGE,
                    icon: PhotoIcon,
                    label: "Image",
                  },
                  {
                    type: ContentTypeEnum.VIDEO_LINK,
                    icon: VideoCameraIcon,
                    label: "Video",
                  },
                  {
                    type: ContentTypeEnum.FILE,
                    icon: PaperClipIcon,
                    label: "File",
                  },
                  {
                    type: ContentTypeEnum.TEXT_BLOCK,
                    icon: DocumentTextIcon,
                    label: "Text",
                  },
                ].map(({ type, icon: Icon, label }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      setContentType(type);
                      setFile(null);
                      setVideoUrl("");
                      setTextContent("");
                    }}
                    className={clsx(
                      "flex flex-col items-center p-3 rounded-lg border-2 transition-colors",
                      contentType === type
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-200 hover:border-gray-300 text-gray-600",
                    )}
                  >
                    <Icon className="h-6 w-6 mb-1" />
                    <span className="text-xs font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* File Upload / Video URL / Rich Text Editor */}
            {contentType === ContentTypeEnum.TEXT_BLOCK ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content
                </label>
                <RichTextEditor
                  content={textContent}
                  onChange={setTextContent}
                  placeholder="Start writing your formatted text..."
                />
              </div>
            ) : contentType === ContentTypeEnum.VIDEO_LINK ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video URL
                </label>
                <input
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://vimeo.com/... or https://youtube.com/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Supports YouTube, Vimeo, and Wistia
                </p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload File
                </label>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={clsx(
                    "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
                    isDragging
                      ? "border-blue-500 bg-blue-50"
                      : file
                        ? "border-green-500 bg-green-50"
                        : "border-gray-300 hover:border-gray-400",
                  )}
                >
                  {file ? (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircleIcon className="h-6 w-6 text-green-500" />
                      <span className="text-sm text-gray-700">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => setFile(null)}
                        className="text-gray-500 hover:text-red-500"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <CloudArrowUpIcon className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">
                        Drag and drop or{" "}
                        <label className="text-blue-600 hover:text-blue-700 cursor-pointer">
                          browse
                          <input
                            type="file"
                            accept={getAcceptedFileTypes()}
                            onChange={handleFileInputChange}
                            className="hidden"
                          />
                        </label>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {getFileTypeDescription()}
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter a title"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-gray-500 hover:text-red-500"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder="Add a tag"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAddTag}
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Visibility */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isPublic"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="isPublic" className="text-sm text-gray-700">
                Make this content publicly accessible (for 3rd party apps)
              </label>
            </div>

            {/* Processing Progress */}
            {isProcessing && processingProgress && (
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-700">
                    Processing PDF...
                  </span>
                  <span className="text-sm text-blue-600">
                    Page {processingProgress.currentPage} of{" "}
                    {processingProgress.totalPages}
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${processingProgress.percent}%` }}
                  />
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  Converting pages to images. Please wait...
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={isLoading}>
                {isProcessing
                  ? "Processing..."
                  : isLoading
                    ? contentType === ContentTypeEnum.TEXT_BLOCK
                      ? "Creating..."
                      : "Uploading..."
                    : contentType === ContentTypeEnum.TEXT_BLOCK
                      ? "Create"
                      : "Upload"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
